import { Component, Input, ViewChild, OnDestroy } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { ButtonComponent } from 'components/input/button/button.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialType } from 'enums/CredentialType';
import { MessageType } from 'enums/MessageType';
import { CrayonProductModel } from 'models/generated/Distribution.Api.Models.Csp.CrayonProduct';
import { CustomerTenantModel } from 'models/generated/Distribution.Api.Models.Csp.CustomerTenant';
import { SubscriptionModel } from 'models/generated/Distribution.Api.Models.Csp.Subscription';
import { SubscriptionDetailsModel } from 'models/generated/Distribution.Api.Models.Csp.SubscriptionDetails';
import { CustomerTenantRequestModel } from 'models/generated/Distribution.Api.Models.CustomerTenantRequest';
import { MicrosoftSubscriptionModel } from 'models/generated/Distribution.Api.Models.MicrosoftSubscription';
import { MicrosoftTenantModel } from 'models/generated/Distribution.Api.Models.MicrosoftTenant';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ProductPlanTagModel } from 'models/generated/Entities.GormCrm.ProductPlanTag';
import { ProductPlanPreparationRequestModel } from 'models/generated/Models.ProductPlanPreparationRequest';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription, firstValueFrom } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { CredentialService } from 'services/credential.service';
import { CspService } from 'services/csp.service';
import { NotificationService } from 'services/notification.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { CompanyMicrosoft365EditComponent } from './company-microsoft365-edit.component';
import { ComponentUrl, UrlParam, NavigationService } from 'services/navigation.service';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { FreeProductPlanSettingDtoModel } from 'models/generated/Models.FreeProductPlanSettingDto';
import { SelectProductPlanComponent } from 'components/accounting/product-plan/select-product-plan.component';
import { CompanyProductsComponent } from 'components/company/company-products.component';
import { CrayonSubscriptionStatus } from 'enums/CrayonSubscriptionStatus';
import { CompanyMicrosoft365AttachToPlanComponent } from './company-microsoft365-attach-to-plan.component';
import { CompanyMicrosoft365SelectBrandComponent } from './company-microsoft365-select-brand.component';
import { DateService } from 'services/date.service';
import { SelectListItemModel } from 'models/SelectList';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { HubAction } from 'enums/HubAction';
import { AccountingPlanStatus } from 'enums/AccountingPlanStatus';
import { ProductPlanDimensions } from 'constants/ProductPlanDimensions';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';

@Component({
	selector: 'company-microsoft365',
	templateUrl: './company-microsoft365.component.html',
	providers: [CompanyDataProviderFactory],
	imports: [Layout, ListView, CustomInputComponent, CompanyMicrosoft365EditComponent, TranslatePipe, CustomDatePipe, ProductNamePipe]
})
@ComponentUrl('company-microsoft365')
export class CompanyMicrosoft365Component implements OnDestroy {
	@ViewChild('assignTenantToCompanyButton') assignTenantToCompanyButton!: ButtonComponent;
	@ViewChild('createTenantButton') createTenantButton!: ButtonComponent;
	@ViewChild('createFreeMs365Button') createFreeMs365Button?: ButtonComponent;
	@ViewChild('createProductPlanAndFreeMs365Button') createProductPlanAndFreeMs365Button!: ButtonComponent;

	dataProvider?: CompanyDataProvider;
	searchString = '';
	searchingTenants = false;
	microsoftTenant?: MicrosoftTenantModel;
	microsoftCustomerAgreementConsented?: boolean;
	customerTenant?: CustomerTenantModel;
	selectedTenant?: CustomerTenantModel;
	selectedSubscription?: SubscriptionDetailsModel;
	subscriptionCache: { [subscriptionId: string]: SubscriptionDetailsModel } = {};
	loadingSubscription = false;
	tenantList?: CustomerTenantModel[];
	private onCrayonChanges: Subscription;
	private onProductPlanChanges: Subscription;
	private changeSubscription?: Subscription;
	private accountingPlanChangesSubscription?: Subscription;
	editPermission = false;
	attachToExistingPlanPermission = false;
	attachToNewPlanPermission = false;
	freeMS365Settings: FreeProductPlanSettingDtoModel[] = [];
	productPlans: ProductPlanModel[] = [];

	crayonProducts: CrayonProductModel[] = [];
	crayonSubscriptions: SubscriptionModel[] = [];
	availableSubscriptions: SubscriptionModel[] = [];
	activeProductPlanSubscriptions: ProductPlanSubscriptionsViewModel[] = [];
	upcomingProductPlanSubscriptions: ProductPlanSubscriptionsViewModel[] = [];
	microsoftSubscriptions?: MicrosoftSubscriptionModel[];

	selectedProductPlanSubscription?: ProductPlanSubscriptionsViewModel;

	loadedTenantInfo = false;
	loadedSubscriptions = false;
	freeMS365Eligible = false;
	credentialTypeEnum = CredentialType;
	crayonSubscriptionStatusEnum = CrayonSubscriptionStatus;

	changeTenantMode: boolean = false;

	private createdProductPlan?: ProductPlanModel;
	timerForError?: NodeJS.Timeout;

	public pendingSearch?: Subscription;
	private currentSearch: { base: string; results: CustomerTenantModel[] } = {
		base: '',
		results: []
	};

	private _companyId?: string;
	@UrlParam
	@Input()
	set companyId(id: string | undefined) {
		this._companyId = id;

		if (!id) return;

		this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);
		this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent).then(viewCompany => viewCompany.companyId = id);
		this.getCompanyTenant();
	}
	get companyId() {
		return this._companyId;
	}

	constructor(
		private readonly dataProviderFactory: CompanyDataProviderFactory,
		private readonly translate: TranslatePipe,
		private readonly cspService: CspService,
		private readonly credentialService: CredentialService,
		private readonly productPlanService: ProductPlanService,
		private readonly pushService: PushService,
		private readonly notificationService: NotificationService,
		private readonly navigation: NavigationService,
		private accountingService: AccountingService,
		readonly dateService: DateService,
		private readonly productNamePipe: ProductNamePipe
	) {
		this.cspService.getAllProducts(true).then(products => {
			this.crayonProducts = products;
		});

		this.onCrayonChanges = this.pushService.onCrayonChanges.subscribe(event => {
			if (this.companyId && this.companyId === event.data.companyId) {
				this.loadedTenantInfo = false;
				this.loadedSubscriptions = false;
				this.getCompanyTenant(true);
			}
		});

		this.onProductPlanChanges = this.pushService.onProductPlanChanges.subscribe(event => {
			if (this.companyId && this.companyId === event.data.companyId) {
				this.loadProductPlanSubscriptions();
			}
		});

		this.editPermission = this.credentialService.hasCredentials(CredentialType.EditMicrosoft365);
		this.attachToExistingPlanPermission = this.credentialService.hasCredentials(CredentialType.EditAccountingPlanEndDate);
		this.attachToNewPlanPermission = this.credentialService.hasCredentials(CredentialType.EditAccountingPlanEndDate, CredentialType.StartProductPlanManually);
	}

	createTenant() {
		if (!this.companyId || !this.dataProvider) return;
		this.dataProvider.getCompany().then(company => {
			if (!company.vatNumber) {
				this.notificationService.error(this.translate.transform('microsoft365.missing-vat-number-on-company'), '');
				this.createTenantButton.reset();
				return;
			}
			if (!company.companyContacts) {
				this.notificationService.error(this.translate.transform('microsoft365.missing-primary-contact'), '');
				this.createTenantButton.reset();
				return;
			}

			const contact = company.companyContacts.find(x => x.contact!.id == company.primaryContactId)?.contact;

			if (!contact) {
				this.notificationService.error(this.translate.transform('microsoft365.missing-primary-contact'), '');
				this.createTenantButton.reset();
				return;
			}
			if (!contact!.contactPhones || contact!.contactPhones.length < 1 || contact!.contactPhones[0].length < 4) {
				this.notificationService.error(this.translate.transform('microsoft365.missing-phonenumber-on-primary-contact'), '');
				this.createTenantButton.reset();
				return;
			}

			if (!(/^[0-9a-zA-Z\.\_\-]+\@[0-9a-zA-Z\.\_\-]{1,}\.[a-zA-Z]{2,4}$/).test(contact!.emailAddress)) {
				this.notificationService.error(this.translate.transform('microsoft365.not-valid-email-on-primary-contact'), '');
				this.createTenantButton.reset();
				return;
			}

			if (!contact!.lastName) {
				this.notificationService.error(this.translate.transform('microsoft365.missing-lastname-on-primary-contact'), '');
				this.createTenantButton.reset();
				return;
			}

			let request = new CustomerTenantRequestModel();

			request.companyId = company.id;
			request.name = company.name;
			request.address = company.address;
			request.city = company.city;
			request.postalCode = company.zipcode;
			request.countryCode = 'DK'; // TODO #14575 company.country must be 2 digits
			request.domain = 'godk' + company.vatNumber;
			request.firstname = contact!.firstName;
			request.lastname = contact!.lastName;
			request.contactEmail = contact!.emailAddress;
			request.contactFirstname = contact!.firstName;
			request.contactLastname = contact!.lastName;
			request.contactPhoneNumber = contact!.contactPhones[0].number;

			this.cspService.createTenant(request)
				.then(() => { this.getCompanyTenant(); })
				.finally(() => { this.createTenantButton.reset(); });
		});
	}

	async getCompanyTenant(reloadCache: boolean = false) {
		if (!this.companyId) return;

		this.microsoftTenant = await this.cspService.getMicrosoftTenantByCompanyId(this.companyId);
		if (!this.microsoftTenant) {
			this.loadedTenantInfo = true;
			this.loadedSubscriptions = true;
			return;
		}

		await Promise.all([
			this.cspService.getCrayonTenantByTenantId(this.microsoftTenant.tenantId).then(tenant => {
				this.customerTenant = tenant;
				this.loadedTenantInfo = true;
			}),
			this.cspService.getCrayonSubscriptions(this.microsoftTenant.tenantId).then(subscriptions => {
				this.crayonSubscriptions = subscriptions;
				this.cacheSubscriptionDetails(subscriptions);
			}),
			this.cspService.checkCustomerTenantMicrosoftCustomerAgreementConsented(this.microsoftTenant.tenantId).then(response => {
				this.microsoftCustomerAgreementConsented = response;
			})
		]);
		await this.loadProductPlanSubscriptions(reloadCache);
		this.loadedSubscriptions = true;
	}

	async loadProductPlanSubscriptions(reloadCache: boolean = false) {
		if (!this.dataProvider) return;

		this.productPlans = await this.dataProvider.getAllProductPlansAndAccountingPlans(reloadCache);
		const ms365ProductPlans = this.productPlans.filter(pp => pp.id && pp.productPlanTags.some(ppt => ppt.tag === ProductPlanTagConstants.microsoft365));

		this.checkIfFreeMS365IsEligible(this.productPlans, ms365ProductPlans);

		this.microsoftSubscriptions = await this.cspService.getSubscriptionsByProductPlanIds(ms365ProductPlans.map(x => x.id!));
		this.availableSubscriptions = this.crayonSubscriptions.filter(cs => !this.microsoftSubscriptions!.find(ms => ms.subscriptionId == cs.id));

		this.upcomingProductPlanSubscriptions = [];
		this.activeProductPlanSubscriptions = [];

		// match subscriptions with product plans and build lists
		this.microsoftSubscriptions.forEach(ms => {
			const productPlan = ms365ProductPlans.find(pp => pp.id == ms.productPlanId); // finding matching product plan
			if (!productPlan) return;

			const crayonSub = this.crayonSubscriptions.find(s => s.id == ms.subscriptionId);

			if (productPlan.deliveryDate) {
				this.activeProductPlanSubscriptions.push({ productPlan: productPlan, subscription: ms, status: (crayonSub) ? 'microsoft365.column.status.' + crayonSub!.status : 'microsoft365.column.status.pending', crayonSubscription: crayonSub } as ProductPlanSubscriptionsViewModel);
			} else {
				this.upcomingProductPlanSubscriptions.push({ productPlan: productPlan, subscription: ms, status: 'microsoft365.column.status.' + (ms.initialOrderData ? 'prepared' : 'missing-data'), crayonSubscription: crayonSub } as ProductPlanSubscriptionsViewModel);
			}
		});

		let selectedProductPlanSubscription = this.upcomingProductPlanSubscriptions.concat(this.activeProductPlanSubscriptions).find(p => p.productPlan.id === this.selectedProductPlanSubscription?.productPlan.id);
		if (selectedProductPlanSubscription) this.selectProductPlanSubscription(selectedProductPlanSubscription);

		if (this.changeSubscription) this.changeSubscription.unsubscribe();
		this.changeSubscription = this.pushService.onChanges<{ microsoftSubscriptionId: string, companyId: string, productPlanId: string }>(MessageType.MicrosoftSubscriptionChanged).subscribe(event => {
			if (event.data?.companyId == this.companyId || (this.productPlans && this.productPlans.find(pp => pp.id && event.data?.productPlanId == pp.id)) || (this.microsoftSubscriptions && this.microsoftSubscriptions.find(ms => ms.id && event.data?.microsoftSubscriptionId === ms.id))) {
				this.loadProductPlanSubscriptions(true);
			}
		});
	}

	getActiveAccountingPlanReferenceNoFromProductPlan(productPlan: ProductPlanModel) {
		return this.accountingService.filterActiveAccountingPlan(productPlan.accountingPlans).referenceNo!;
	}

	checkIfFreeMS365IsEligible(productPlans: ProductPlanModel[], ms365ProductPlans: ProductPlanModel[]) {
		this.createFreeMs365Button?.reset();

		this.productPlanService.getFreeProductPlanSettings(ProductPlanTagConstants.microsoft365).then(freeMS365Settings => {
			let allowedFreeAmount = 0;
			this.freeMS365Settings = freeMS365Settings;
			freeMS365Settings.forEach(s => {
				const ppsWithTag = productPlans.filter(pp => pp.id && pp.productPlanTags.some(ppt => ppt.tag === s.sourceTag));
				allowedFreeAmount += s.amount * ppsWithTag.length;
			});

			const freeProductPlansMS365 = ms365ProductPlans.filter(pp => pp.annualValueForRenewal === 0);
			this.freeMS365Eligible = freeProductPlansMS365.length < allowedFreeAmount;
		});
	}

	searchTenants() {
		let query = this.searchString.trim();
		this.selectedTenant = undefined;

		if (query.length < 2) {
			this.tenantList = [];
			return;
		}

		if (this.currentSearch.base !== '' && query.substring(0, this.currentSearch.base.length) === this.currentSearch.base) {
			if (!this.pendingSearch) this.filterResults(query);
			return;
		}
		if (this.pendingSearch) this.pendingSearch.unsubscribe();
		this.currentSearch.base = query.substring(0, 2);
		this.tenantList = [];
		this.searchingTenants = true;
		this.pendingSearch = this.cspService.searchTenant(this.currentSearch.base).subscribe((results: CustomerTenantModel[]) => {
			this.currentSearch.results = results;
			this.filterResults(query);
			this.pendingSearch = undefined;
		});
	}

	private filterResults(query: string) {
		this.cspService.getMicrosoftTenants().then(microsoftTenants => {
			this.tenantList = this.currentSearch.results.filter(tenant => {
				return tenant.name.toLowerCase().indexOf(query.toLowerCase()) >= 0 && !microsoftTenants.some(mt => mt.tenantId == tenant.id);
			});
			this.searchingTenants = false;
		});
	}

	assignSelectedTenantToCompany() {
		if (!this.selectedTenant) return;

		this.assignTenantToCompanyButton.working = true;
		this.loadedTenantInfo = false;
		this.loadedSubscriptions = false;
		this.cspService.assignTenantToCustomer(this.companyId!, this.selectedTenant.id).then(() => {
			this.getCompanyTenant();
		});

		this.tenantList = [];
		this.searchString = '';
		this.cancelChangeTenant();
	}

	async selectParentAndCreateFreeMicrosoft365() {
		if (!await firstValueFrom(this.notificationService.confirm('microsoft365.create-free-ms365.confirmation-message')))
			return this.createFreeMs365Button?.reset();
		let productPlans = await this.dataProvider?.getAllProductPlansAndAccountingPlans();
		if (!productPlans)
			return this.createFreeMs365Button?.reset();
		const freeMS365TagSet = new Set(this.freeMS365Settings.map(s => s.sourceTag));
		const productPlansWithTagAndActiveAccountingPlan: ProductPlanModel[] = productPlans
			.filter(p => p.productPlanTags?.some(t => freeMS365TagSet.has(t.tag)))
			.filter(p => {
				const active = this.accountingService.filterActiveAccountingPlan(p.accountingPlans);
				return this.accountingService.isActive(active);
			});

		switch (productPlansWithTagAndActiveAccountingPlan.length) {
			case 1:
				this.createFreeMicrosoft365(productPlansWithTagAndActiveAccountingPlan[0].id!);
				break;
			default: {
				this.navigation.openPopup(SelectProductPlanComponent, 'microsoft365.free-ms365.select-parent.header').then(view => {
					view.headline = 'microsoft365.free-ms365.select-parent.headline';
					view.label = 'microsoft365.free-ms365.select-parent.label';
					view.productPlans = productPlansWithTagAndActiveAccountingPlan;
					view.required = true;
					view.productPlanSelected.subscribe(async selectedPlanId => {
						if (this.createFreeMs365Button) this.createFreeMs365Button.working = true;
						await this.createFreeMicrosoft365(selectedPlanId);
						this.navigation.closeView(view);
						this.createFreeMs365Button?.succeeded();
					});
					this.createFreeMs365Button?.reset();
				});
				break;
			}
		}
	}

	async createFreeMicrosoft365(parentProductPlanId: string) {
		const productPlanCreateRequest = await this.generateProductPlanPreparationRequest(0, parentProductPlanId);
		await this.productPlanService.createProductPlan(productPlanCreateRequest);
	}

	async assignSubscriptionToNewFreeMicrosoft365ProductPlan() {
		if (!this.selectedSubscription || !this.selectedSubscription.endDate) return;
		const productName = this.selectedSubscription!.productName + (this.selectedSubscription!.quantity > 1 ? ` - ${this.selectedSubscription!.quantity} ${this.translate.transform('general.pieces')}` : '');

		this.navigation.openPopup(CompanyMicrosoft365SelectBrandComponent, 'microsoft365.free-ms365.select-brand.header').then(view => {
			view.productName = productName;
			view.brandSelected.subscribe(async selectedBrandId => {
				const planValue = 0;
				const productPlanCreateRequest = await this.generateProductPlanPreparationRequest(planValue, undefined, selectedBrandId);

				productPlanCreateRequest.productPlan!.productName = productName;
				if (!this.selectedSubscription!.autoRenewEnabled!) {
					productPlanCreateRequest.productPlan!.terminationDate = this.dateService.today();
				}

				const acceptableWaitTime = 10000; // 10 seconds, if the process takes longer than this, something is likely wrong, and we want to stop waiting for the push notification and throw an error instead

				this.accountingPlanChangesSubscription = this.pushService.onChanges<{ accountingPlanId: string, productPlanId: string; companyId: string }>(MessageType.AccountingPlanChanges).subscribe(event => {
					if (event.data?.companyId == this.companyId && event.action == HubAction.Insert) {
						this.checkForCreatedAccountingPlan(event.data?.accountingPlanId, event.data?.productPlanId, planValue);
					}
				});

				this.createdProductPlan = await this.productPlanService.createProductPlan(productPlanCreateRequest);

				this.timerForError = setTimeout(() => {
					this.accountingPlanChangesSubscription?.unsubscribe();
					this.notificationService.error('microsoft365.subscription-details.create-product-plan.error-process-took-too-long', 'Creating product plan and assigning subscription took too long, likely due to missing push notification. Please check if the product plan was created and the subscription was assigned, and if not, try again.')
				}, acceptableWaitTime);

				this.navigation.closeView(view);
			});
		});
	}

	async checkForCreatedAccountingPlan(createdAccountingPlanId: string, createdAccountingPlanProductPlanId: string, planValue: number) {
		// Failover if eventbus message is received quicker than createProductPlan response
		if (!this.createdProductPlan) {
			setTimeout(() => this.checkForCreatedAccountingPlan(createdAccountingPlanId, createdAccountingPlanProductPlanId, planValue), 50);
			return;
		}
		if (this.createdProductPlan.id! === createdAccountingPlanProductPlanId) {
			this.accountingPlanChangesSubscription?.unsubscribe();
			this.changeSubscription?.unsubscribe(); // unsubscribe to avoid double loads, will reset in loadProductPlanSubscriptions
			clearTimeout(this.timerForError);

			const productPlan = this.createdProductPlan;
			let endDate = this.dateService.getDate(this.selectedSubscription!.endDate!);

			await this.cspService.assignSubscriptionToExistingProductPlan(this.selectedSubscription!.id!, productPlan.id!, endDate, productPlan.terminationDate != undefined, this.selectedSubscription!.autoRenewEnabled!);
			await this.accountingService.registerStartOfPlan(createdAccountingPlanId);
			await this.productPlanService.setProductPlanRestrictEndDate(productPlan.id!, endDate, true);
			await this.accountingService.changeAccountingPlanEndDate(createdAccountingPlanId, endDate, planValue);
			await this.accountingService.setPlanStatus(AccountingPlanStatus.Control, AccountingPlanStatus.None, createdAccountingPlanId);

			this.selectedSubscription = undefined;
			await this.loadProductPlanSubscriptions(true);
		}
	}

	async generateProductPlanPreparationRequest(planValue: number, parentProductPlanId?: string, brandId?: string) {
		const productPlanCreateRequest = new ProductPlanPreparationRequestModel();

		productPlanCreateRequest.productPlan = this.generateFreeMicrosoft365ProductPlan(planValue, parentProductPlanId);
		if (brandId) productPlanCreateRequest.productPlan.brandId = brandId;
		productPlanCreateRequest.accountingPlanSetupId = await this.getMicrosoft365AccountingPlanSetupId();
		productPlanCreateRequest.totalPlanValue = planValue;
		productPlanCreateRequest.companyDimension = 'GO';
		productPlanCreateRequest.departmentDimension = 'BALLERUP (HELE)';
		if (!productPlanCreateRequest.productPlan.brandId) throw new Error('BrandId not found'); 

		return productPlanCreateRequest;
	}

	generateFreeMicrosoft365ProductPlan(planValue: number, parentProductPlanId?: string): ProductPlanModel {
		let productPlan = new ProductPlanModel();

		productPlan.productPlanTags.push({ tag: ProductPlanTagConstants.microsoft365 } as ProductPlanTagModel);
		productPlan.include(p => p.productPlanTags);
		productPlan.companyId = this.companyId;
		productPlan.productName = 'Microsoft 365 - Exchange Online (Plan 1)';
		productPlan.units = 1;
		productPlan.nextPeriod = 12;
		productPlan.unitPrice = planValue;
		productPlan.annualValueForRenewal = planValue;
		productPlan.autoRenewable = true;

		if (parentProductPlanId) {
			const parentProductPlan = this.productPlans.find(pp => pp.id === parentProductPlanId);
			if (!parentProductPlan) throw new Error('ProductPlan not found');
			productPlan.parentId = parentProductPlan?.id;
			productPlan.brandId = parentProductPlan?.brandId;
		}

		return productPlan;
	}

	async getMicrosoft365AccountingPlanSetupId() {
		return (await this.accountingService.getAccountingPlanSetups(true)).find(a => a.productDimensionCode == ProductPlanDimensions.microsoft365)?.id;
	}

	cancelChangeTenant() {
		this.changeTenantMode = false;
	}

	selectCrayonSubscription(subscription: SubscriptionModel | undefined, resetSelectedProductPlanSubscription: boolean = true) {
		if (resetSelectedProductPlanSubscription) this.selectedProductPlanSubscription = undefined;
		if (!subscription) {
			this.selectedSubscription = undefined;
			this.loadingSubscription = false;
			return;
		}
		if (subscription.status == CrayonSubscriptionStatus.Active) {
			if (this.subscriptionCache[subscription.id]) {
				this.selectedSubscription = this.subscriptionCache[subscription.id];
			} else {
				this.selectedSubscription = new SubscriptionDetailsModel();
				this.selectedSubscription.id = subscription.id; // Select when cache is available
				this.loadingSubscription = true;
			}
			return;
		}
		this.selectedSubscription = new SubscriptionDetailsModel();
		this.selectedSubscription.id = subscription.id;
		this.loadingSubscription = true;
		this.cspService.getSubscriptionDetails(subscription.id).then(details => {
			if (this.selectedSubscription?.id !== details.id) return;
			this.loadingSubscription = false;
			this.selectedSubscription = details;
		});
	}

	async cacheSubscriptionDetails(subscriptions: SubscriptionModel[]) {
		let subscriptionIds = subscriptions.filter(s => s.status == CrayonSubscriptionStatus.Active).flatMap(s => s.id)
		this.subscriptionCache = {};
		subscriptionIds.forEach(subscriptionId => {
			this.cspService.getSubscriptionDetails(subscriptionId).then(details => {
				this.subscriptionCache[subscriptionId] = details;
				if (this.selectedSubscription?.id == details.id) {
					this.selectedSubscription = details;
					this.loadingSubscription = false;
				}
			});
		})
	}

	navigateToProductPlan() {
		if (!this.selectedSubscription?.id || !this.dataProvider?.companyId) return;
		var productPlanId = this.microsoftSubscriptions?.find(ms => ms.subscriptionId == this.selectedSubscription?.id)?.productPlanId;
		this.navigation.refreshView(CompanyProductsComponent).then(view => {
			view.companyId = this.dataProvider?.companyId!;
			view.selectedProductPlanId = productPlanId;
		});
	}

	selectProductPlanSubscription(productPlanSubscription: ProductPlanSubscriptionsViewModel) {
		this.selectedProductPlanSubscription = productPlanSubscription;
		this.selectCrayonSubscription(productPlanSubscription.crayonSubscription, false);
	}

	async attachSubscriptionToExistingProductPlan() {
		if (!this.selectedSubscription || this.selectedProductPlanSubscription || !this.dataProvider) return;
		const availableProductPlans = this.productPlans
			.filter(pp => pp.id && pp.deliveryDate && pp.autoRenewable)
			.filter(pp => pp.autoRenewalDate && pp.autoRenewalDate! >= this.dateService.today())
			.filter(pp => !this.microsoftSubscriptions?.filter(ms => ms.subscriptionId != undefined).map(ms => ms.productPlanId).includes(pp.id))
			.filter(pp => pp.productPlanTags.some(ppt => ppt.tag === ProductPlanTagConstants.microsoft365) || pp.accountingPlans?.some((ap: AccountingPlanModel) => ap.accountingPlanSetup?.productDimensionCode == ProductPlanDimensions.microsoft365))
			.sort((a, b) => a.productName.localeCompare(b.productName));

		let posts: LedgerPostModel[];
		posts = await this.dataProvider.getLedgerPostsByProductPlanIds(availableProductPlans.map(pp => pp.id!));

		this.navigation.openPopup(CompanyMicrosoft365AttachToPlanComponent, 'microsoft365.attach-to-product-plan.header').then(view => {
			view.availableProductPlans = availableProductPlans.map(pp => new SelectListItemModel(pp, this.productNamePipe.transform(pp, true, true)));
			view.selectedSubscription = this.selectedSubscription!;
			view.ledgerPosts = posts;
			view.productPlanAttached.subscribe(async () => {
				this.selectedSubscription = undefined;
				await this.loadProductPlanSubscriptions(true);
				this.navigation.closeView(view);
			});
		});
	}

	ngOnDestroy() {
		this.onCrayonChanges.unsubscribe();
		this.onProductPlanChanges.unsubscribe();
		this.changeSubscription?.unsubscribe();
	}
}

export interface ProductPlanSubscriptionsViewModel {
	productPlan: ProductPlanModel;
	subscription: MicrosoftSubscriptionModel;
	crayonSubscription?: SubscriptionModel;
	status: string;
}
